import { useEffect, useRef, useState } from 'react';
import { Camera, Trash2, User } from 'lucide-react';
import { Button } from '@/design-system/Button';
import { cn } from '@/lib/cn';

/**
 * Choosing a profile photograph, with a preview.
 *
 * Holds a `File` rather than uploading immediately, because a photo picked while creating somebody
 * has nowhere to go until that person exists. The dialog creates the employee, then uploads.
 *
 * The checks here are for fast feedback only. The server re-checks the size and determines the
 * format from the file's own leading bytes, so a renamed script is rejected there regardless of
 * what this accepts - `File.type` comes from the operating system's guess at the extension and is
 * not evidence of anything.
 */
const MAX_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** Fast client-side feedback on a chosen photo; null when it is acceptable. */
export function checkPhotoFile(file: File): string | null {
  if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) return 'Choose a JPEG, PNG or WebP image.';
  if (file.size > MAX_BYTES) {
    return `That image is ${(file.size / 1024 / 1024).toFixed(1)} MB; the limit is 5 MB.`;
  }
  return null;
}

export function PhotoPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The preview is an object URL and has to be revoked, or each re-pick leaks the previous image.
  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const pick = (file: File | undefined) => {
    setError(null);
    if (!file) return;

    const problem = checkPhotoFile(file);
    if (problem) {
      setError(problem);
      return;
    }
    onChange(file);
  };

  return (
    <div className="space-y-1.5">
      <span className="block text-small font-medium text-text-primary">Photo</span>

      <div className="flex items-center gap-4">
        <span
          aria-hidden
          className={cn(
            'grid size-20 shrink-0 place-items-center overflow-hidden rounded-full border border-border-default',
            preview ? '' : 'bg-surface-sunken text-text-muted',
          )}
        >
          {preview ? (
            <img src={preview} alt="" className="size-full object-cover" />
          ) : (
            <User className="size-8" />
          )}
        </span>

        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
              leadingIcon={<Camera className="size-4" />}
            >
              {value ? 'Choose another' : 'Choose a photo'}
            </Button>
            {value && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => {
                  onChange(null);
                  // Without this, re-picking the same file fires no change event.
                  if (inputRef.current) inputRef.current.value = '';
                }}
                leadingIcon={<Trash2 className="size-4" />}
              >
                Remove
              </Button>
            )}
          </div>
          <p className="text-caption text-text-muted">
            {value ? value.name : 'JPEG, PNG or WebP, up to 5 MB. Optional.'}
          </p>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_PHOTO_TYPES.join(',')}
        className="sr-only"
        aria-label="Choose a profile photo"
        onChange={(event) => pick(event.target.files?.[0])}
      />

      {error && (
        <p role="alert" className="text-caption text-danger-text">
          {error}
        </p>
      )}
    </div>
  );
}
